import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';

function countryCodeToFlag(countryCode) {
    if (!countryCode || countryCode.length !== 2) return '';
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
}

@observer
class F1DriverInfoDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            stats: null,
            loading: true,
            hoveredRace: null,
            tooltipX: 0,
            tooltipY: 0,
            showTable: false,
        };
    }

    componentDidMount() {
        this.loadStats();
    }

    loadStats = async () => {
        const { driverNumber, year, sessionKey } = this.props;
        try {
            const query = { driverNumber };
            if (year) {
                query.year = year;
            }
            if (sessionKey) {
                query.sessionKey = sessionKey;
            }
            const response = await util.fetchApi('/api/f1drivers/stats', { method: 'GET' }, query);
            if (response && response.status === 200) {
                this.setState({ stats: response.data, loading: false });
            } else {
                this.setState({ loading: false });
            }
        } catch (e) {
            this.setState({ loading: false });
        }
    }

    getDriverFromList() {
        const { driverNumber, driverList = [] } = this.props;
        const num = parseInt(driverNumber, 10);
        return driverList.find(d => parseInt(d.driverNumber || d.driver_number, 10) === num) || {};
    }

    handleDotEnter = (race, x, y) => {
        this.setState({ hoveredRace: race, tooltipX: x, tooltipY: y });
    }

    handleDotLeave = () => {
        this.setState({ hoveredRace: null });
    }

    toggleTable = () => {
        this.setState(prev => ({ showTable: !prev.showTable }));
    }

    close = () => {
        const { drawerLevel = 1, stores } = this.props;
        const { appState } = stores;
        appState.closeDrawer(null, drawerLevel);
    }

    render() {
        const { stats, loading } = this.state;
        const driver = this.getDriverFromList();
        const teamHistory = stats ? stats.teamHistory || [] : [];

        // Use driver info from driverList prop, or from stats teamHistory (latest year)
        const latestRecord = teamHistory.length > 0 ? teamHistory[0] : {};
        const fullName = driver.fullName || driver.full_name || latestRecord.fullName || '';
        const teamName = driver.teamName || driver.team_name || latestRecord.teamName || '';
        const teamColour = driver.teamColour || driver.team_colour || latestRecord.teamColour || '999999';
        const headshotUrl = driver.headshotUrl || driver.headshot_url || latestRecord.headshotUrl || '';
        const countryCode = driver.countryCode || driver.country_code || latestRecord.countryCode || '';
        const driverNumber = driver.driverNumber || driver.driver_number || this.props.driverNumber;
        const nameAcronym = driver.nameAcronym || driver.name_acronym || latestRecord.nameAcronym || '';
        const championshipPosition = latestRecord.championshipPosition || '';
        const championshipPoints = latestRecord.championshipPoints || '';

        return (
            <div class='px-3 pb-4'>
                {/* Header with headshot and driver info */}
                <div class='d-flex align-items-start mb-3'>
                    {headshotUrl ? (
                        <img
                            src={headshotUrl}
                            alt={fullName}
                            class='rounded me-3'
                            style='width: 100px; height: 100px; object-fit: cover; background: #222;'
                        />
                    ) : (
                        <div class='rounded me-3 d-flex align-items-center justify-content-center bg-body-secondary'
                            style='width: 100px; height: 100px;'>
                            <span class='text-body-secondary' style='font-size: 2rem; font-weight: 700;'>{nameAcronym || '#'}</span>
                        </div>
                    )}
                    <div class='flex-grow-1'>
                        <h4 class='mb-1'>
                            {countryCode && <span class='me-2'>{countryCodeToFlag(countryCode)}</span>}
                            {fullName}
                        </h4>
                        <div class='d-flex align-items-center mb-1'>
                            <span
                                class='d-inline-block me-2 rounded'
                                style={`width: 6px; height: 20px; background-color: #${teamColour};`}
                            />
                            <span class='text-body-secondary'>{teamName}</span>
                        </div>
                        <span class='badge bg-body-secondary text-body' style='font-size: 1rem;'>#{driverNumber}</span>
                    </div>
                </div>

                {/* Team color accent bar */}
                <div class='rounded mb-3' style={`height: 4px; background-color: #${teamColour};`} />

                {/* Championship */}
                {(championshipPosition || championshipPoints) && (
                    <div class='d-flex gap-3 mb-3'>
                        {championshipPosition && (
                            <div class='text-center'>
                                <div class='text-body-secondary' style='font-size: 0.75rem;'>Championship</div>
                                <div class='fw-bold' style='font-size: 1.2rem;'>P{championshipPosition}</div>
                            </div>
                        )}
                        {championshipPoints !== '' && (
                            <div class='text-center'>
                                <div class='text-body-secondary' style='font-size: 0.75rem;'>Points</div>
                                <div class='fw-bold' style='font-size: 1.2rem;'>{championshipPoints}</div>
                            </div>
                        )}
                    </div>
                )}

                {/* Loading spinner */}
                {loading && (
                    <div class='text-center py-3'>
                        <i class='fa-solid fa-spinner fa-spin text-body-secondary' />
                        <small class='text-body-secondary ms-2'>Loading stats...</small>
                    </div>
                )}

                {/* Stats row */}
                {stats && (
                    <div>
                        <div class='d-flex gap-2 mb-3'>
                            <div class='card flex-fill'>
                                <div class='card-body py-2 px-3 text-center'>
                                    <div class='fw-bold' style='font-size: 1.4rem;'>{stats.wins}</div>
                                    <div class='text-body-secondary' style='font-size: 0.75rem;'>Wins</div>
                                </div>
                            </div>
                            <div class='card flex-fill'>
                                <div class='card-body py-2 px-3 text-center'>
                                    <div class='fw-bold' style='font-size: 1.4rem;'>{stats.podiums}</div>
                                    <div class='text-body-secondary' style='font-size: 0.75rem;'>Podiums</div>
                                </div>
                            </div>
                            <div class='card flex-fill'>
                                <div class='card-body py-2 px-3 text-center'>
                                    <div class='fw-bold' style='font-size: 1.4rem;'>{stats.totalRaces}</div>
                                    <div class='text-body-secondary' style='font-size: 0.75rem;'>Races</div>
                                </div>
                            </div>
                        </div>

                        {/* Race Results - Position Trend Graph */}
                        {stats.raceResults && stats.raceResults.length > 0 && (() => {
                            const { hoveredRace, tooltipX, tooltipY, showTable } = this.state;
                            const chronological = [...stats.raceResults].reverse();
                            const raceOnly = chronological.filter(r => r.sessionType !== 'sprint');
                            const data = raceOnly.length > 0 ? raceOnly : chronological;

                            const svgWidth = 500;
                            const svgHeight = 250;
                            const padLeft = 35;
                            const padRight = 15;
                            const padTop = 20;
                            const padBottom = 25;
                            const chartW = svgWidth - padLeft - padRight;
                            const chartH = svgHeight - padTop - padBottom;
                            const maxPos = 20;

                            const xScale = (i) => padLeft + (data.length > 1 ? (i / (data.length - 1)) * chartW : chartW / 2);
                            const yScale = (pos) => padTop + ((pos - 1) / (maxPos - 1)) * chartH;

                            const gridPositions = [1, 5, 10, 15, 20];
                            const points = data.map((r, i) => ({ x: xScale(i), y: yScale(r.position), race: r }));
                            const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
                            const lineColour = `#${teamColour}`;

                            return (
                                <div class='mb-3'>
                                    <h6 class='mb-2'>Position Trend</h6>
                                    <div style='position: relative;'>
                                        <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} style='width: 100%; height: auto;'>
                                            {/* Podium zone */}
                                            <rect
                                                x={padLeft} y={padTop}
                                                width={chartW} height={yScale(3) - padTop + ((yScale(4) - yScale(3)) / 2)}
                                                fill={lineColour} opacity='0.07'
                                            />

                                            {/* Grid lines and Y labels */}
                                            {gridPositions.map(pos => (
                                                <g key={pos}>
                                                    <line
                                                        x1={padLeft} y1={yScale(pos)}
                                                        x2={svgWidth - padRight} y2={yScale(pos)}
                                                        stroke='currentColor' stroke-opacity='0.15' stroke-dasharray='3,3'
                                                    />
                                                    <text
                                                        x={padLeft - 8} y={yScale(pos) + 4}
                                                        text-anchor='end' fill='currentColor' opacity='0.5'
                                                        style='font-size: 10px;'
                                                    >P{pos}</text>
                                                </g>
                                            ))}

                                            {/* Trend line */}
                                            {data.length > 1 && (
                                                <path d={linePath} fill='none' stroke={lineColour} stroke-width='2.5' stroke-linejoin='round' stroke-linecap='round' />
                                            )}

                                            {/* Data dots */}
                                            {points.map((p, i) => {
                                                const isPodium = p.race.position <= 3;
                                                const isWin = p.race.position === 1;
                                                return (
                                                    <circle
                                                        key={i}
                                                        cx={p.x} cy={p.y}
                                                        r={isWin ? 5 : isPodium ? 4 : 3}
                                                        fill={lineColour}
                                                        stroke={isPodium ? (isWin ? '#FFD700' : '#fff') : 'none'}
                                                        stroke-width={isPodium ? 1.5 : 0}
                                                        style='cursor: pointer;'
                                                        onMouseEnter={() => this.handleDotEnter(p.race, p.x, p.y)}
                                                        onMouseLeave={this.handleDotLeave}
                                                        onTouchStart={() => this.handleDotEnter(p.race, p.x, p.y)}
                                                        onTouchEnd={this.handleDotLeave}
                                                    />
                                                );
                                            })}

                                            {/* X-axis year labels */}
                                            {(() => {
                                                const yearLabels = [];
                                                let lastYear = null;
                                                data.forEach((r, i) => {
                                                    if (r.year !== lastYear) {
                                                        yearLabels.push({ year: r.year, x: xScale(i) });
                                                        lastYear = r.year;
                                                    }
                                                });
                                                return yearLabels.map(yl => (
                                                    <text
                                                        key={yl.year}
                                                        x={yl.x} y={svgHeight - 5}
                                                        text-anchor='start' fill='currentColor' opacity='0.5'
                                                        style='font-size: 10px;'
                                                    >{yl.year}</text>
                                                ));
                                            })()}
                                        </svg>

                                        {/* Tooltip */}
                                        {hoveredRace && (
                                            <div
                                                class='card shadow-sm'
                                                style={`position: absolute; pointer-events: none; z-index: 10; padding: 6px 10px; font-size: 0.75rem; white-space: nowrap; transform: translate(-50%, -110%); left: ${(tooltipX / svgWidth) * 100}%; top: ${(tooltipY / svgHeight) * 100}%;`}
                                            >
                                                <strong>P{hoveredRace.position}</strong>
                                                {' '}
                                                {hoveredRace.countryCode && <span>{countryCodeToFlag(hoveredRace.countryCode)} </span>}
                                                {hoveredRace.circuitShortName || hoveredRace.raceName}
                                                <span class='text-body-secondary ms-1'>{hoveredRace.year}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Collapsible table toggle */}
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-link text-body-secondary p-0 mt-1'
                                        onClick={this.toggleTable}
                                    >
                                        <i class={`fa-solid fa-chevron-${showTable ? 'up' : 'down'} me-1`} />
                                        {showTable ? 'Hide' : 'Show'} detailed results ({stats.raceResults.length})
                                    </button>

                                    {showTable && (
                                        <div style='max-height: 400px; overflow-y: auto;' class='mt-2'>
                                            <table class='table table-sm mb-0'>
                                                <thead style='position: sticky; top: 0; z-index: 1;' class='bg-body'>
                                                    <tr>
                                                        <th style='font-size: 0.75rem;'>Pos</th>
                                                        <th style='font-size: 0.75rem;'>Race</th>
                                                        <th style='font-size: 0.75rem;'>Team</th>
                                                        <th style='font-size: 0.75rem;'>Year</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {stats.raceResults.map((r, idx) => (
                                                        <tr key={idx}>
                                                            <td>
                                                                <span class='d-flex align-items-center'>
                                                                    <span
                                                                        class='d-inline-block me-1 rounded'
                                                                        style={`width: 3px; height: 16px; background-color: #${r.teamColour || '999'};`}
                                                                    />
                                                                    <span class='fw-bold'>{r.position}</span>
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span style='font-size: 0.85rem;'>
                                                                    {r.countryCode && <span class='me-1'>{countryCodeToFlag(r.countryCode)}</span>}
                                                                    {r.circuitShortName || r.raceName}
                                                                    {r.sessionType === 'sprint' && (
                                                                        <span class='badge bg-warning text-dark ms-1' style='font-size: 0.6rem;'>Sprint</span>
                                                                    )}
                                                                </span>
                                                            </td>
                                                            <td><span class='text-body-secondary' style='font-size: 0.8rem;'>{r.teamName}</span></td>
                                                            <td><span class='text-body-secondary' style='font-size: 0.8rem;'>{r.year}</span></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* Team History */}
                        {teamHistory.length > 0 && (
                            <div>
                                <h6 class='mb-2'>Team History</h6>
                                {teamHistory.map((record, idx) => (
                                    <div key={idx} class='d-flex align-items-center mb-1'>
                                        <span class='badge bg-body-secondary text-body me-2' style='min-width: 45px;'>{record.year}</span>
                                        <span
                                            class='d-inline-block me-2 rounded'
                                            style={`width: 4px; height: 16px; background-color: #${record.teamColour || '999'};`}
                                        />
                                        <span class='flex-grow-1'>{record.teamName}</span>
                                        {record.championshipPosition && (
                                            <span class='text-body-secondary' style='font-size: 0.8rem;'>
                                                P{record.championshipPosition}
                                                {record.championshipPoints ? ` (${record.championshipPoints} pts)` : ''}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

export default F1DriverInfoDrawer;
